import React, { ComponentProps } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useClipboard,
} from "@chakra-ui/react";
import { Certificate } from "./Certificate";
import { FaLinkedinIn } from "@react-icons/all-files/fa/FaLinkedinIn";
import { FaTwitter } from "@react-icons/all-files/fa/FaTwitter";
import { FiChevronDown } from "@react-icons/all-files/fi/FiChevronDown";
import { FiDownload } from "@react-icons/all-files/fi/FiDownload";
import { FiLink } from "@react-icons/all-files/fi/FiLink";
import { TwitterShareButton } from "react-share";
import { pdf } from "@react-pdf/renderer";
import type { PageSize } from "@react-pdf/types";

interface ShareCertificationProps {
  title: string;
  certId: string;
  certUrl: string;
  certificateProps: ComponentProps<typeof Certificate>;
}

export default function ShareCertification({
  title,
  certId,
  certUrl,
  certificateProps,
}: ShareCertificationProps) {
  const { hasCopied, onCopy } = useClipboard(certUrl);

  const today = new Date();
  const params = new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    organizationId: "28602935",
    issueYear: today.getFullYear().toString(),
    issueMonth: (today.getMonth() + 1).toString(), // January is month 0
    name: title,
    certId,
    certUrl,
  }).toString();

  function downloadPdf(size: PageSize) {
    return async function () {
      const blob = await pdf(
        <Certificate {...certificateProps} size={size} />
      ).toBlob();
      open(URL.createObjectURL(blob), "_blank");
      window.gtag?.("event", "certificate_download");
    };
  }

  return (
    <Stack align="flex-start" ml="-4">
      <Button
        resetButtonStyle={false}
        url={certUrl}
        title={`I just completed the ${title} certification on Odyssey, the @ApolloGraphQL learning platform.`}
        as={TwitterShareButton}
        variant="ghost"
        leftIcon={<FaTwitter />}
        onClick={() => window.gtag?.("event", "share_cert_on_twitter")}
      >
        Share on Twitter
      </Button>
      <Button
        as="a"
        target="_blank"
        variant="ghost"
        href={`https://www.linkedin.com/profile/add?${params}`}
        leftIcon={<FaLinkedinIn />}
        onClick={() => window.gtag?.("event", "linkedin_button_click")}
      >
        Add to LinkedIn profile
      </Button>
      <Menu>
        <MenuButton
          as={Button}
          variant="ghost"
          leftIcon={<FiDownload />}
          rightIcon={<FiChevronDown />}
        >
          Download PDF
        </MenuButton>
        <MenuList>
          <MenuItem onClick={downloadPdf("LETTER")}>US Letter</MenuItem>
          <MenuItem onClick={downloadPdf("A4")}>A4</MenuItem>
        </MenuList>
      </Menu>
      <Button variant="ghost" leftIcon={<FiLink />} onClick={onCopy}>
        {hasCopied ? "Copied!" : "Copy link"}
      </Button>
    </Stack>
  );
}
